import { debounce } from 'lodash-es';
import {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  type RefObject,
} from 'react';

import {
  type ObservedSize,
  useResizeObserver,
} from '../../../core/hooks/useResizeObserver.js';

/**
 * Handles resizing of the DataTable root container.
 * @returns root ref
 * @internal
 */
export function useTableRootResize(
  remeasureRows: (virtualizationContainerWidth?: number) => void,
): RefObject<HTMLDivElement | null> {
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Keep track of the root container size. We are updating a reference to
  // the container size with the actual size.
  const rootContainerSize = useRef<number>(
    Math.round(rootRef.current?.getBoundingClientRect().width ?? 0),
  );
  //  Store debounced function in ref to cancel on unmount
  const debouncedResizeRef = useRef<ReturnType<typeof debounce>>(undefined);
  // Triggers DataTable re-renders via React state rather than alien-signals atoms,
  // avoiding "Cannot update during rendering" warnings from the stale inst.value
  // window that would otherwise open between LINE A (setOptions flush) and LINE B
  // (useSyncExternalStore snapshot update) in the subsequent re-render.
  const [, forceRender] = useReducer((x: number, _: void): number => x + 1, 0);

  const onContainerResize = useCallback(
    (size: ObservedSize) => {
      if (size.width) {
        rootContainerSize.current = Math.round(size.width);
        // The `table.remeasureRow` is a memoized function, which will only run if
        // its parameters change.
        remeasureRows(rootContainerSize.current);
        // Trigger a re-render to recalculate styles that depend on container
        // dimensions, such as overflow behavior.
        forceRender();
      }
    },
    [remeasureRows, forceRender],
  );

  useEffect(() => {
    // Debounce the resizing to avoid flickering and performance impacts
    // during resize. The rows are measured once the DataTable width stabilizes
    // for a given number of milliseconds.
    debouncedResizeRef.current = debounce(onContainerResize, 125);
    return () => debouncedResizeRef.current?.cancel();
  }, [onContainerResize]);

  useResizeObserver({
    ref: rootRef,
    onResize: (size) => debouncedResizeRef.current?.(size),
  });

  // When the table re-renders, invoke the measureRow with the last known
  // value for the containerSize in order to keep the memoization stable.
  remeasureRows(rootContainerSize.current);

  return rootRef;
}
